/**
 * <SuccessPhase> — celebration step that fires after a successful
 * /reconcile/apply. Shows the headline stats, prompts the user to
 * share, then closes on "Done".
 *
 * Why this exists: the previous flow toast-and-closed immediately, which
 * is fine for the "I just want to be done" path but skips the obvious
 * organic-reach moment. People want to share productive feelings;
 * reconciling 60 transactions in 90 seconds is one of those moments.
 */
import React from 'react';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import SocialShareButtons from '../SocialShareButtons';

const StatPill = ({ value, label, testId }) => (
  value > 0 ? (
    <div className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3 min-w-[100px]" data-testid={testId}>
      <div className="text-2xl font-extrabold text-[#020617]">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold mt-0.5">{label}</div>
    </div>
  ) : null
);

const SHARE_TEXT_TEMPLATES = [
  'Just reconciled my month in 90 seconds with Forge Ledger ⚡',
  'Forge Ledger just matched my whole bank statement against my budget. Wild how fast that was.',
  'Finally caught up on months of reconciliation — Forge Ledger did the heavy lifting.',
];

const SuccessPhase = ({ stats, onDone }) => {
  const { reconciled = 0, paired = 0, imported = 0 } = stats || {};
  // Pick a deterministic template per render so it doesn't flicker if
  // React re-renders mid-display.
  const text = React.useMemo(
    () => SHARE_TEXT_TEMPLATES[Math.floor(Math.random() * SHARE_TEXT_TEMPLATES.length)],
    [],
  );

  return (
    <div className="space-y-6" data-testid="reconcile-success-phase">
      <div className="flex flex-col items-center text-center pt-2">
        <div className="h-14 w-14 rounded-full bg-[#DCFCE7] flex items-center justify-center mb-3">
          <CheckCircle2 className="text-[#16A34A]" size={32} />
        </div>
        <h3 className="text-2xl font-extrabold text-[#020617]">Reconciled.</h3>
        <p className="text-sm text-[#64748B] mt-1 max-w-md">
          Your transactions are now matched to your bank statement.
          Your projections will reflect the real numbers from here on.
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-3" data-testid="reconcile-success-stats">
        <StatPill value={reconciled} label="Reconciled" testId="reconcile-success-stat-reconciled" />
        <StatPill value={paired} label="Paired" testId="reconcile-success-stat-paired" />
        <StatPill value={imported} label="Imported" testId="reconcile-success-stat-imported" />
      </div>

      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
        <div className="flex items-start gap-2 mb-3">
          <Sparkles className="text-[#0052FF] mt-0.5" size={16} />
          <div>
            <div className="text-sm font-semibold text-[#020617]">Tell a friend?</div>
            <p className="text-xs text-[#64748B] mt-0.5">
              If Forge Ledger just saved you a chunk of time, sharing helps
              other people find it — and you earn £1 of credit for every
              friend who subscribes.
            </p>
          </div>
        </div>
        <SocialShareButtons
          text={text}
          url="https://forgeledger.net"
          hashtag="ForgeLedger"
          testIdPrefix="reconcile-success-share"
          className="justify-start"
        />
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={onDone}
          className="bg-[#0052FF] text-white hover:bg-[#003ECC] rounded-lg px-6"
          data-testid="reconcile-success-done-button"
        >
          Done
        </Button>
      </div>
    </div>
  );
};

export default SuccessPhase;
